import { motion } from 'framer-motion';
import { ArrowRight, Users, Target, Award, Sparkles, TrendingUp, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import { FaqSection } from '@/components/FaqSection';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const ABOUT_FAQS = [
  {
    q: 'Wer steht hinter marKIpilot?',
    a: 'marKIpilot wird von der MYSYMP AG mit Sitz an der Oberdorfstrasse 18a in 6207 Nottwil (Schweiz) betrieben, eingetragen im Handelsregister unter CHE-192.170.455.'
  },
  {
    q: 'Was bietet marKIpilot an?',
    a: 'Eine App-Marketing-Plattform mit Pay-per-Install-Modell (Fixpreis nur für verifizierte Downloads) und 16 spezialisierten KI-Marketing-Agenten für Strategie, Brand, Content, Social, Performance, CRM, Produkt, PR und Analytics.'
  },
  {
    q: 'Wie erreiche ich marKIpilot?',
    a: 'Per E-Mail an info@markipilot.com. Für eine erste Einschätzung deines Einsparpotenzials steht ausserdem der kostenlose ROI-Rechner auf markipilot.com/roi-rechner zur Verfügung.'
  },
  {
    q: 'Für wen ist marKIpilot gedacht?',
    a: 'Für App-Publisher, Marketing-Agenturen, In-House-Marketing-Teams und Unternehmensberater, die App-Installs planbar skalieren und Akquisitionskosten senken wollen.'
  },
];

const teamMembers = [
  {
    name: "Fabio Marugg",
    role: "Founder & CEO",
    expertise: "App Growth Strategy, Performance Marketing",
    description: "15+ Jahre Erfahrung in App Marketing und User Acquisition für führende FinTech und Gaming Apps."
  },
  {
    name: "Sarah Müller",
    role: "Head of Strategy",
    expertise: "Corporate Strategy, Business Development",
    description: "Erfahrene Strategieberaterin mit Fokus auf digitale Transformation und Wachstumsstrategien."
  },
  {
    name: "Marc Weber",
    role: "Head of Creative",
    expertise: "Creative Strategy, UGC & Performance Creative",
    description: "Creative Director mit 500+ erfolgreichen Performance Creatives für Top-Marken."
  },
  {
    name: "Lisa Chen",
    role: "Head of Data & Analytics",
    expertise: "Data Science, Attribution, KI-Integration",
    description: "Data Scientist mit Expertise in Marketing Analytics und KI-gestützter Optimierung."
  }
];

const values = [
  {
    icon: Target,
    title: "Performance-First",
    description: "Jede Entscheidung wird durch Daten und messbare Ergebnisse getrieben."
  },
  {
    icon: Sparkles,
    title: "KI-Powered",
    description: "Wir nutzen modernste KI-Technologie, um Marketing-Exzellenz zu skalieren."
  },
  {
    icon: TrendingUp,
    title: "Kontinuierliche Innovation",
    description: "Wir bleiben am Puls der Zeit und integrieren neue Kanäle und Methoden."
  },
  {
    icon: Globe,
    title: "Transparenz & Partnership",
    description: "Volle Transparenz und echte Partnerschaften mit unseren Kunden."
  }
];

const milestones = [
  { year: "2018", title: "Gründung von marKIpilot", description: "Start als spezialisierte App Marketing Agentur in Zürich" },
  { year: "2020", title: "50+ erfolgreiche Apps", description: "Expansion in DACH-Region mit Fokus auf FinTech und Gaming" },
  { year: "2023", title: "KI-Integration", description: "Launch der marKI-Plattform mit 16 spezialisierten KI-Agenten" },
  { year: "2026", title: "Marktführer in App Growth", description: "Über 100 Apps betreut, 500M+ generierte Installs" }
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Seo
        title="Über uns – Das Team hinter marKIpilot"
        description="marKIpilot ist die KI-App-Marketing-Plattform der MYSYMP AG aus Nottwil (Schweiz). Lerne das Team und unsere Performance-First-Werte kennen."
        path="/about"
      />
      {/* Hero */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-slate-50 opacity-80" />
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 30%, rgba(20,184,166,0.08) 0%, transparent 50%)`,
        }} />
        
        <motion.div 
          className="relative max-w-6xl mx-auto text-center"
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6"
            variants={fadeInUp}
          >
            <Users className="w-4 h-4 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">Über marKIpilot</span>
          </motion.div>
          
          <motion.h1 
            className="text-5xl md:text-6xl font-bold text-slate-900 mb-6"
            variants={fadeInUp}
          >
            Dein Partner für{' '}
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              nachhaltiges App-Wachstum
            </span>
          </motion.h1>
          
          <motion.p 
            className="text-xl text-slate-600 max-w-3xl mx-auto mb-8"
            variants={fadeInUp}
          >
            Seit 2018 helfen wir App-Unternehmen, profitabel zu skalieren – durch datengetriebene Strategien, 
            KI-gestützte Optimierung und kreative Exzellenz.
          </motion.p>
          
          <motion.div className="flex gap-4 justify-center" variants={fadeInUp}>
            <Link to="/login">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700" data-testid="get-started-btn">
                Jetzt starten <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/brand">
              <Button size="lg" variant="outline" data-testid="brand-assets-btn">
                Brand Assets
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Company Story */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
          >
            <motion.h2 className="text-3xl font-bold text-slate-900 mb-6" variants={fadeInUp}>
              Unsere Geschichte
            </motion.h2>
            <motion.p className="text-lg text-slate-600 mb-4" variants={fadeInUp}>
              marKIpilot wurde 2018 aus der Überzeugung gegründet, dass App-Marketing mehr als nur Media Buying ist. 
              Es geht um die perfekte Orchestrierung von Strategie, Kreativität, Technologie und Daten.
            </motion.p>
            <motion.p className="text-lg text-slate-600 mb-4" variants={fadeInUp}>
              Was als spezialisierte Beratung für FinTech-Apps begann, ist heute eine führende Full-Service-Agentur 
              für App-Wachstum im DACH-Raum. Unser Team aus ehemaligen Führungskräften von Agenturen, Tech-Konzernen 
              und Beratungshäusern bringt über 80 Jahre kombinierte Erfahrung mit.
            </motion.p>
            <motion.p className="text-lg text-slate-600" variants={fadeInUp}>
              2023 haben wir mit der marKI-Plattform eine neue Ära eingeleitet: KI-gestützte Marketing-Intelligenz, 
              die unseren Kunden 24/7 zur Verfügung steht. 16 spezialisierte KI-Agenten, trainiert auf Tausenden 
              erfolgreichen Kampagnen, demokratisieren Marketing-Exzellenz.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Unsere Meilensteine</h2>
            <p className="text-lg text-slate-600">Der Weg von der Vision zur Realität</p>
          </motion.div>
          
          <motion.div 
            className="grid md:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {milestones.map((milestone, i) => (
              <motion.div 
                key={i} 
                className="relative"
                variants={fadeInUp}
              >
                <div className="bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-colors h-full">
                  <div className="text-4xl font-bold text-teal-600 mb-2">{milestone.year}</div>
                  <h3 className="font-semibold text-slate-900 mb-2">{milestone.title}</h3>
                  <p className="text-sm text-slate-600">{milestone.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Unser Team</h2>
            <p className="text-lg text-slate-600">Experten mit kombiniert 80+ Jahren Erfahrung</p>
          </motion.div>
          
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {teamMembers.map((member, i) => (
              <motion.div 
                key={i} 
                className="group"
                variants={fadeInUp}
              >
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 hover:border-teal-300 transition-all hover:shadow-lg h-full">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-teal-400 to-cyan-500 mx-auto mb-4 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white" data-testid={`team-member-initials-${i}`}>
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <h3 className="font-bold text-slate-900 text-center mb-1">{member.name}</h3>
                  <p className="text-sm text-teal-600 font-medium text-center mb-2">{member.role}</p>
                  <p className="text-xs text-slate-500 text-center mb-3">{member.expertise}</p>
                  <p className="text-sm text-slate-600 text-center">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Unsere Werte</h2>
            <p className="text-lg text-slate-600">Was uns antreibt und auszeichnet</p>
          </motion.div>
          
          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            {values.map((value, i) => {
              const Icon = value.icon;
              return (
                <motion.div 
                  key={i}
                  className="bg-white rounded-xl p-6 border border-slate-200 hover:border-teal-300 transition-all hover:shadow-md"
                  variants={fadeInUp}
                >
                  <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{value.title}</h3>
                  <p className="text-sm text-slate-600">{value.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      <FaqSection faqs={ABOUT_FAQS} title="Häufige Fragen zu marKIpilot & MYSYMP AG" />

      {/* CTA */}
      <section className="py-20 px-6 bg-gradient-to-br from-teal-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bereit, gemeinsam zu wachsen?
            </h2>
            <p className="text-xl text-teal-50 mb-8">
              Lass uns sprechen und herausfinden, wie wir deine App zum Erfolg führen können.
            </p>
            <Link to="/login">
              <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50" data-testid="cta-start-btn">
                Kostenlos starten <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
